'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Check, Plus } from 'lucide-react';
import type { Product } from '@/models';
import { useCart } from '@/hooks/useCart';
import { formatPrice } from '@/lib/utils';
import { resolvePrice } from '@/presenters/price.presenter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface NativeProductCardProps {
  product: Product;
}

export function NativeProductCard({ product }: NativeProductCardProps) {
  const { addItem } = useCart();
  const price = resolvePrice(product);
  const [justAdded, setJustAdded] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) {
        clearTimeout(resetTimer.current);
      }
    };
  }, []);

  // Single badge from real flags only: manual bestseller toggle wins,
  // otherwise the featured (`عرض خاص`) flag. Never fabricate rating/old-price.
  const badge = product.isBestseller ? 'الأكثر مبيعاً' : product.isFeatured ? 'عرض خاص' : null;

  const handleAdd = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    addItem({
      productId: product.id,
      variantId: product.variants[0]?.id ?? 'default',
      productName: product.name,
      variantName: product.variants[0]?.nameAr ?? '',
      price,
      showPrice: product.showPrice,
      imageUrl: product.imageUrl,
    });
    // Press micro-animation is the only feedback: stays on list,
    // no toast/sheet/navigation.
    setJustAdded(true);
    if (resetTimer.current) {
      clearTimeout(resetTimer.current);
    }
    resetTimer.current = setTimeout(() => setJustAdded(false), 700);
  };

  return (
    <Link href={`/products/${product.slug}`} dir="rtl" className="group block">
      <Card size="sm" className="h-full transition-all pt-0 group-hover:ring-primary/40 rounded-sm">
        <div className="relative aspect-square p-4 pt-0">
          <Image
            src={product.imageUrl || '/products/galaxy-s24-ultra.svg'}
            alt={product.name}
            fill
            className="object-contain "
            unoptimized
          />
          {badge && (
            <Badge className="absolute top-2 right-2">{badge}</Badge>
          )}
        </div>
        <CardContent className="space-y-1">
          <p className="line-clamp-1 text-sm font-bold">{product.name}</p>
          <p className="text-xs font-medium text-muted-foreground">{product.brandName}</p>
          <div className="flex items-center justify-between pt-1">
            {product.showPrice ? (
              <p className="text-sm font-extrabold">{formatPrice(price)} دج</p>
            ) : (
              <p className="text-xs text-muted-foreground">السعر عند الطلب</p>
            )}
            <Button
              type="button"
              size="icon-sm"
              onClick={handleAdd}
              aria-label="أضف إلى السلة"
              data-added={justAdded || undefined}
              className="transition-transform duration-150 active:scale-90 data-added:scale-110"
            >
              {justAdded ? <Check /> : <Plus />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
